import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { ContactFilters } from '@/types/contact';

export type SelectionMode = 'none' | 'explicit' | 'all_matching';

export interface BulkSelectionState {
  mode: SelectionMode;
  selectedIds: Set<string>;
  excludedIds: Set<string>;
  filterState: ContactFilters | null;
  totalMatchingCount: number;
}

export interface UseBulkSelectionReturn {
  mode: SelectionMode;
  selectedIds: Set<string>;
  excludedIds: Set<string>;
  filterState: ContactFilters | null;
  totalMatchingCount: number;
  selectAll: (filterState: ContactFilters, count: number) => void;
  clearSelection: () => void;
  toggleId: (id: string) => void;
  isSelected: (id: string) => boolean;
  effectiveCount: number;
  setExplicitSelection: (ids: Set<string>) => void;
}

/**
 * Hook for managing bulk selection state with support for:
 * - Explicit selection (selecting individual IDs)
 * - "All matching" selection (filter-based selection with exclusions)
 *
 * When in 'all_matching' mode, the selection is represented by filter criteria
 * plus an exclusion list, rather than storing potentially thousands of IDs.
 */
export function useBulkSelection(
  currentFilters?: ContactFilters
): UseBulkSelectionReturn {
  const [state, setState] = useState<BulkSelectionState>({
    mode: 'none',
    selectedIds: new Set(),
    excludedIds: new Set(),
    filterState: null,
    totalMatchingCount: 0,
  });

  // Track previous filters to detect changes
  const prevFiltersRef = useRef<string | null>(null);

  // Clear selection when filters change (only in all_matching mode)
  useEffect(() => {
    if (state.mode === 'all_matching' && currentFilters) {
      const currentFilterJson = JSON.stringify(currentFilters);
      const savedFilterJson = state.filterState ? JSON.stringify(state.filterState) : null;

      // If filters changed from what we saved when selecting all, clear selection
      if (savedFilterJson && currentFilterJson !== savedFilterJson) {
        setState({
          mode: 'none',
          selectedIds: new Set(),
          excludedIds: new Set(),
          filterState: null,
          totalMatchingCount: 0,
        });
      }
    }
    // Update ref for next comparison
    if (currentFilters) {
      prevFiltersRef.current = JSON.stringify(currentFilters);
    }
  }, [currentFilters, state.mode, state.filterState]);

  const selectAll = useCallback((filterState: ContactFilters, count: number) => {
    setState({
      mode: 'all_matching',
      selectedIds: new Set(),
      excludedIds: new Set(),
      filterState: { ...filterState }, // Snapshot the filter state
      totalMatchingCount: count,
    });
  }, []);

  const clearSelection = useCallback(() => {
    setState({
      mode: 'none',
      selectedIds: new Set(),
      excludedIds: new Set(),
      filterState: null,
      totalMatchingCount: 0,
    });
  }, []);

  const setExplicitSelection = useCallback((ids: Set<string>) => {
    setState({
      mode: ids.size > 0 ? 'explicit' : 'none',
      selectedIds: new Set(ids),
      excludedIds: new Set(),
      filterState: null,
      totalMatchingCount: 0,
    });
  }, []);

  const toggleId = useCallback((id: string) => {
    setState(prev => {
      if (prev.mode === 'all_matching') {
        // In all_matching mode, toggling adds/removes from exclusion list
        const newExcluded = new Set(prev.excludedIds);
        if (newExcluded.has(id)) {
          newExcluded.delete(id);
        } else {
          newExcluded.add(id);
        }
        return { ...prev, excludedIds: newExcluded };
      } else {
        // In explicit or none mode, toggle the selection
        const newSelected = new Set(prev.selectedIds);
        if (newSelected.has(id)) {
          newSelected.delete(id);
        } else {
          newSelected.add(id);
        }
        return {
          ...prev,
          mode: newSelected.size > 0 ? 'explicit' : 'none',
          selectedIds: newSelected,
        };
      }
    });
  }, []);

  const isSelected = useCallback((id: string): boolean => {
    if (state.mode === 'all_matching') {
      // In all_matching mode, selected unless excluded
      return !state.excludedIds.has(id);
    }
    // In explicit mode, selected only if in the set
    return state.selectedIds.has(id);
  }, [state.mode, state.excludedIds, state.selectedIds]);

  const effectiveCount = useMemo(() => {
    if (state.mode === 'all_matching') {
      return state.totalMatchingCount - state.excludedIds.size;
    }
    return state.selectedIds.size;
  }, [state.mode, state.totalMatchingCount, state.excludedIds.size, state.selectedIds.size]);

  return {
    ...state,
    selectAll,
    clearSelection,
    toggleId,
    isSelected,
    effectiveCount,
    setExplicitSelection,
  };
}
